import { Application } from "../../App/Models/Application";
import { StartupActionType } from "../Actions/StartupActions";
import { Company } from "../../Company/Models/Company";
import { ResponseError, LoadingState } from "../../Common";
import { Topic } from "../../Topic/Models/Topic";
import { FeedbackParent } from "../../Feedback/Constants/feedbackEnums";
import { BreadcrumbData } from "../../Layout";
import { TopicManagerAssignment } from "../../Topic/Models/TopicManagerAssignment";
import { UserTopicAssignment } from "../../Topic/Models/UserTopicAssignment";

export interface StartupState {
  parentType: FeedbackParent;
  activeApp?: Application;
  activeTopic?: Topic;
  applications: Application[];
  appsLoadingState: LoadingState;
  appsLoadingError: ResponseError;
  company?: Company;
  companyLoadingError: ResponseError;
  companyLoadingState: LoadingState;
  topics: Topic[];
  topicsLoadingError: ResponseError;
  topicsLoadingState: LoadingState;
  breadcrumbs: BreadcrumbData[];
  path?: string;
  topicManagers: Record<string, TopicManagerAssignment[]>;
  topicManagersLoadingState: Record<string, LoadingState>;
  topicManagersError: Record<string, ResponseError>;
  topicUsers: Record<string, UserTopicAssignment[]>;
  topicUsersLoadingState: Record<string, LoadingState>;
  topicUsersError: Record<string, ResponseError>;
}

const initialLoadingState = {
  isLoaded: false,
  isLoading: false,
  errored: false
};

const initialError = {
  errorDescription: "",
  errorResult: ""
};

const initalState: StartupState = {
  parentType: FeedbackParent.Application,
  activeApp: {
    id: 2,
    name: "cmThread",
    authId: 4,
    rolePrefix: "IM",
    allowResourceRequest: false
  },
  applications: [],
  topics: [],
  appsLoadingState: { ...initialLoadingState },
  companyLoadingState: { ...initialLoadingState },
  topicsLoadingState: { ...initialLoadingState },
  appsLoadingError: { ...initialError },
  companyLoadingError: { ...initialError },
  topicsLoadingError: { ...initialError },
  breadcrumbs: [],
  topicManagers: {},
  topicManagersLoadingState: {},
  topicManagersError: {},
  topicUsers: {},
  topicUsersLoadingState: {},
  topicUsersError: {}
};

const updateObject = (oldObject: any, newValues: any) => ({ ...oldObject, ...newValues });

export const startupReducer = (state: StartupState = initalState, action: any) => {
  switch (action.type) {
    case StartupActionType.SET_LOCATION:
      return { ...state, ...{ path: action.payload, breadcrumbs: [] } };
    case StartupActionType.SET_BREADCRUMB:
      return { ...state, ...{ breadcrumbs: action.payload } };
    case StartupActionType.SET_ACTIVE_APP:
      return updateObject(state, {
        activeApp: action.payload,
        activeTopic: undefined,
        parentType: FeedbackParent.Application
      });
    case StartupActionType.SET_APPLICATIONS:
      return updateObject(state, {
        applications: action.payload,
        appsLoadingState: {
          isLoaded: true,
          isLoading: false
        }
      });
    case StartupActionType.SET_LOADING_APPLICATIONS: {
      const newState = updateObject(state, {
        appsLoadingState: updateObject(state.appsLoadingState, {
          isLoading: true
        })
      });
      return newState;
    }
    case StartupActionType.SET_APP_LOADING_ERROR: {
      return updateObject(state, {
        appLoadingError: action.payload,
        appsLoadingState: updateObject(state.appsLoadingState, {
          errored: true
        })
      });
    }

    case StartupActionType.SET_COMPANY: {
      return updateObject(state, {
        company: action.payload,
        companyLoadingState: {
          isLoaded: true,
          isLoading: false
        }
      });
    }
    case StartupActionType.SET_LOADING_COMPANY: {
      return updateObject(state, {
        companyLoadingState: updateObject(state.companyLoadingState, {
          isLoading: true
        })
      });
    }
    case StartupActionType.SET_COMPANY_LOADING_ERROR: {
      return updateObject(state, {
        companyLoadingError: action.payload,
        companyLoadingState: updateObject(state.companyLoadingState, {
          errored: true
        })
      });
    }

    case StartupActionType.SET_TOPICS: {
      return updateObject(state, {
        topics: action.payload,
        topicsLoadingState: {
          isLoaded: true,
          isLoading: false
        }
      });
    }
    case StartupActionType.SET_LOADING_TOPICS: {
      return updateObject(state, {
        topicsLoadingState: updateObject(state.topicsLoadingState, {
          isLoading: true
        })
      });
    }
    case StartupActionType.SET_TOPICS_LOADING_ERROR: {
      return updateObject(state, {
        topicsLoadingError: action.payload,
        topicsLoadingState: updateObject(state.topicsLoadingState, {
          errored: true
        })
      });
    }
    case StartupActionType.ADD_TOPIC: {
      const cloned: Topic[] = Object.assign([], state.topics);
      cloned.push(action.payload);

      return updateObject(state, {
        topics: cloned
      });
    }
    case StartupActionType.UPDATE_TOPIC: {
      return updateObject(state, {
        topics: Object.assign([], action.payload)
      });
    }
    case StartupActionType.SET_ACTIVE_TOPIC: {
      return updateObject(state, {
        activeTopic: action.payload,
        activeApp: undefined,
        parentType: FeedbackParent.Topic
      });
    }
    // Topic User/Manager updates
    case StartupActionType.SET_LOADING_TOPIC_MANAGERS: {
      const topicCode = action.payload;
      return updateObject(state, {
        topicManagersLoadingState: updateObject(state.topicManagersLoadingState, {
          [topicCode]: {
            isLoading: true,
            isLoaded: false,
            errored: false
          }
        })
      });
    }
    case StartupActionType.SET_TOPIC_MANAGERS_ERROR: {
      const { topicCode, error } = action.payload;
      return updateObject(state, {
        topicManagersError: updateObject(state.topicManagersError, {
          [topicCode]: error
        }),
        topicManagersLoadingState: updateObject(state.topicManagersLoadingState, {
          [topicCode]: {
            isLoading: false,
            isLoaded: false,
            errored: true
          }
        })
      });
    }
    case StartupActionType.SET_TOPIC_MANAGERS: {
      const { topicCode, managers } = action.payload;
      return updateObject(state, {
        topicManagers: updateObject(state.topicManagers, {
          [topicCode]: managers
        }),
        topicManagersLoadingState: updateObject(state.topicManagersLoadingState, {
          [topicCode]: {
            isLoading: false,
            isLoaded: true,
            errored: false
          }
        })
      });
    }
    case StartupActionType.SET_LOADING_TOPIC_USERS: {
      const topicCode = action.payload;
      return updateObject(state, {
        topicUsersLoadingState: updateObject(state.topicUsersLoadingState, {
          [topicCode]: {
            isLoading: true,
            isLoaded: false,
            errored: false
          }
        })
      });
    }
    case StartupActionType.SET_TOPIC_USERS_ERROR: {
      const { topicCode, error } = action.payload;
      return updateObject(state, {
        topicUsersError: updateObject(state.topicUsersError, {
          [topicCode]: error
        }),
        topicUsersLoadingState: updateObject(state.topicUsersLoadingState, {
          [topicCode]: {
            isLoading: false,
            isLoaded: false,
            errored: true
          }
        })
      });
    }
    case StartupActionType.SET_TOPIC_USERS: {
      const { topicCode, users } = action.payload;
      return updateObject(state, {
        topicUsers: updateObject(state.topicUsers, {
          [topicCode]: users
        }),
        topicUsersLoadingState: updateObject(state.topicUsersLoadingState, {
          [topicCode]: {
            isLoading: false,
            isLoaded: true,
            errored: false
          }
        })
      });
    }
    default:
      return state;
  }
};

export default startupReducer;
